import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultEQM3020_R01Dto, EQM3020_R01Dto } from './Dto_EQM3020_R01';

/** 계측기 검교정 결과 조회 검색 조건 */
export interface EQM3020_R01Filter {
	year: number;           // 검교정 연도
	equipmentCd: string;    // 계측기 코드
	equipmentNm: string;    // 계측기 명
}

/** 검색 조건 초기값 (연도: 올해, 계측기: 전체) */
export const defaultEQM3020_R01Filter: EQM3020_R01Filter = {
	year: new Date().getFullYear(),
	equipmentCd: '',
	equipmentNm: '',
};

export interface Store_EQM3020_R01 {
	// === 필터 관련 ===
	EQM3020_R01Filter: EQM3020_R01Filter;
	EQM3020_R01PastFilter: EQM3020_R01Filter;  // 마지막 조회에 사용된 검색 조건 (동일 조건 재조회 판별용)
	setEQM3020_R01Filter: (filter: Partial<EQM3020_R01Filter>) => void;
	setEQM3020_R01PastFilter: (filter: Partial<EQM3020_R01Filter>) => void;

	// === 리스트 관련 ===
	EQM3020_R01List: EQM3020_R01Dto[];
	setEQM3020_R01List: (list: EQM3020_R01Dto[]) => void;
	insertEQM3020_R01List: (data: EQM3020_R01Dto[], index?: number) => void;
	removeEQM3020_R01List: (index: number) => void;
	updateEQM3020_R01List: (index: number, data: EQM3020_R01Dto) => void;

	// === 삭제 표시 (소프트 삭제, 저장 시 API 호출) ===
	deletedEQM3020_R01List: string[];
	addDeletedEQM3020_R01List: (idList: string[]) => void;
	removeDeletedEQM3020_R01List: (idList?: string[]) => void;

	// === 그리드 선택 ===
	selectedEQM3020_R01List: string[];
	setSelectedEQM3020_R01List: (idList: string[]) => void;
	addSelectedEQM3020_R01List: (idList: string[]) => void;
	removeSelectedEQM3020_R01List: (idList?: string[]) => void;
	initSelectedEQM3020_R01List: () => void;
	allSelectedEQM3020_R01List: () => void;

	// === 원본 목록 (변경 감지·isModified 비교용) ===
	originalEQM3020_R01List: EQM3020_R01Dto[];
	setOriginalEQM3020_R01List: (list: EQM3020_R01Dto[]) => void;

	// === AG Grid UI 상태 ===
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	allCount: number;
	setAllCount: (allCount: number) => void;

	// === 전체 초기화 ===
	initData: () => void;
}

/**
 * EQM3020_R01 계측기 검교정 결과 등록 Zustand 스토어
 */
export const useEQM3020_R01Store = create<Store_EQM3020_R01>((set) => ({
	EQM3020_R01Filter: defaultEQM3020_R01Filter,
	EQM3020_R01PastFilter: defaultEQM3020_R01Filter,

	/** 검색 조건을 부분 갱신한다. */
	setEQM3020_R01Filter: (update) => set((s) => ({
		EQM3020_R01Filter: { ...s.EQM3020_R01Filter, ...update },
	})),
	/** 마지막 조회에 사용된 검색 조건을 저장한다. */
	setEQM3020_R01PastFilter: (update) => set((s) => ({
		EQM3020_R01PastFilter: { ...s.EQM3020_R01PastFilter, ...update },
	})),

	EQM3020_R01List: [],

	/**
	 * 조회 결과 목록을 설정하고 valueIndex·orderIndex를 보정한다.
	 * - valueIndex: 그리드 행 고유 식별자
	 * - orderIndex / orderIndexSeq: 동일 계측기 내 다건 정렬용
	 */
	setEQM3020_R01List: (list) => set((s) => {
		/** 기본 DTO 병합 후 인덱스가 보정된 목록 */
		const newList: EQM3020_R01Dto[] = [];
		/** valueIndex·orderIndex 자동 부여용 순번 */
		let idx = 1;
		list.forEach((item) => {
			newList.push({
				...defaultEQM3020_R01Dto, ...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : idx,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : idx,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
			});
			idx++;
		});
		return { ...s, EQM3020_R01List: newList };
	}),

	/**
	 * 지정 위치에 신규 행을 삽입하고 orderIndexSeq를 재정렬한다.
	 * @param index 기준 행의 valueIndex (해당 orderIndex 그룹에 삽입)
	 */
	insertEQM3020_R01List: (data, index) => set((s) => {
		/** 삽입 대상이 포함된 전체 목록 복사본 */
		let newList = [...s.EQM3020_R01List];
		/** 기준 행 (index = valueIndex) */
		const target = newList.find((i) => i.valueIndex === index);
		/** 기준 행이 속한 orderIndex 그룹 */
		const targetOrderIndex = target?.orderIndex || 0;
		/** 동일 orderIndex 내 삽입 순서 */
		const newSeq = (target?.orderIndexSeq || 0) + 1;
		/** 신규 행에 부여할 valueIndex 시작값 */
		let maxValueIndex = Math.max(0, ...newList.map((i) => i.valueIndex || 0)) + 1;

		/** 동일 orderIndex 그룹 행 (seq 재정렬 대상) */
		const targetRows = newList.filter((i) => i.orderIndex === targetOrderIndex);
		targetRows.forEach((i) => { if (i.orderIndexSeq >= newSeq) i.orderIndexSeq += 1; });
		newList = newList.filter((i) => i.orderIndex !== targetOrderIndex);
		newList.push(...targetRows);

		/** 삽입할 신규 행 (valueIndex·orderIndex·orderIndexSeq 부여) */
		const newData = data.map((item) => ({
			...defaultEQM3020_R01Dto, ...item,
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newSeq,
		}));

		if (index !== undefined) newList.splice(index, 0, ...newData);
		else newList.push(...newData);

		return { ...s, EQM3020_R01List: newList };
	}),

	/** valueIndex 기준으로 그리드 행을 제거한다. */
	removeEQM3020_R01List: (index) => set((s) => ({
		...s, EQM3020_R01List: s.EQM3020_R01List.filter((i) => i.valueIndex !== index),
	})),

	/** valueIndex에 해당하는 단일 행을 갱신한다. */
	updateEQM3020_R01List: (valueIndex, data) => set((s) => ({
		...s,
		EQM3020_R01List: s.EQM3020_R01List.map((i) => i.valueIndex === valueIndex ? data : i),
	})),

	deletedEQM3020_R01List: [],

	/**
	 * 삭제 표시 대상 valueIndex 목록에 추가한다.
	 * - inserted·isModified 신규/수정 행은 제외 (저장 전 로컬만 제거)
	 */
	addDeletedEQM3020_R01List: (idList) => set((s) => {
		/** 기존 삭제 표시 목록 복사본 */
		const deleted = [...s.deletedEQM3020_R01List];
		for (const v of idList) {
			/** 신규 등록(inserted) 행 여부 */
			const inserted = s.EQM3020_R01List.find((i) => i.valueIndex === Number(v) && i.inserted);
			/** 수정(isModified) 행 여부 */
			const modified = s.EQM3020_R01List.find((i) => i.valueIndex === Number(v) && i.isModified);
			if (!inserted && !modified) deleted.push(v);
		}
		return { ...s, deletedEQM3020_R01List: deleted };
	}),

	/** 삭제 표시를 취소한다. idList 미지정 시 전체 취소. */
	removeDeletedEQM3020_R01List: (idList) => set((s) => ({
		...s,
		deletedEQM3020_R01List: s.deletedEQM3020_R01List.filter(
			(v) => (idList ? !idList.includes(v) : false)),
	})),

	selectedEQM3020_R01List: [],
	setSelectedEQM3020_R01List: (idList) => set({ selectedEQM3020_R01List: idList }),
	addSelectedEQM3020_R01List: (idList) => set((s) => ({
		...s, selectedEQM3020_R01List: [...s.selectedEQM3020_R01List, ...idList],
	})),
	removeSelectedEQM3020_R01List: (idList) => set((s) => ({
		...s,
		selectedEQM3020_R01List: s.selectedEQM3020_R01List.filter(
			(v) => (idList ? !idList.includes(v) : false)),
	})),
	initSelectedEQM3020_R01List: () => set({ selectedEQM3020_R01List: [] }),
	allSelectedEQM3020_R01List: () => set((s) => ({
		...s, selectedEQM3020_R01List: s.EQM3020_R01List.map((i) => i.valueIndex.toString()),
	})),

	originalEQM3020_R01List: [],

	/** 저장 전 원본 목록을 보관한다. (변경 감지·isModified 비교용) */
	setOriginalEQM3020_R01List: (list) => set((s) => {
		/** 인덱스가 순차 부여된 원본 스냅샷 */
		const newList: EQM3020_R01Dto[] = [];
		let idx = 1;
		list.forEach((item) => {
			newList.push({
				...defaultEQM3020_R01Dto, ...item,
				valueIndex: idx, orderIndex: idx, orderIndexSeq: idx,
			});
			idx++;
		});
		return { ...s, originalEQM3020_R01List: newList };
	}),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	/** 화면 store 상태를 초기화한다. */
	initData: () => set((s) => ({
		...s,
		EQM3020_R01Filter: defaultEQM3020_R01Filter,
		EQM3020_R01PastFilter: defaultEQM3020_R01Filter,
		EQM3020_R01List: [],
		selectedEQM3020_R01List: [],
		deletedEQM3020_R01List: [],
		allCount: 0,
		scrollTop: null,
	})),
}));

/** 그리드 날짜 셀(GridCalendar) 팝업 상태 */
export interface Store_EQM3020_R01_GridCalendarState {
	EQM3020_R01GridCalendarState: {
		pickerVisible: boolean;                              // 달력 표시 여부
		pickerPosition: { top: number; left: number };       // 달력 팝업 위치
		targetRowIndex: number | null;                       // 편집 대상 행 인덱스
		targetField: string | null;                          // 편집 대상 필드명 (calibDt 등)
		targetDate: Date | undefined;                        // 현재 선택된 날짜
	};
	setEQM3020_R01GridCalendarState: (state: Partial<Store_EQM3020_R01_GridCalendarState['EQM3020_R01GridCalendarState']>) => void;
	initEQM3020_R01GridCalendarState: () => void;
}

/** 교정일자·차기교정일 등 날짜 컬럼용 GridCalendar 전용 스토어 */
export const useEQM3020_R01Store_GridCalendar = create<Store_EQM3020_R01_GridCalendarState>((set) => ({
	EQM3020_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined,
	},
	/** 달력 팝업 상태를 부분 갱신한다. */
	setEQM3020_R01GridCalendarState: (newState) => set((state) => ({
		...state,
		EQM3020_R01GridCalendarState: { ...state.EQM3020_R01GridCalendarState, ...newState },
	})),
	/** 달력 팝업 상태를 초기값으로 되돌린다. */
	initEQM3020_R01GridCalendarState: () => set((state) => ({
		...state,
		EQM3020_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined,
		},
	})),
}));
